import React, { useState } from "react";
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
} from "react-native";
import { observer } from "mobx-react";
import { useTranslation } from "react-i18next";
import { Ionicons, Feather, AntDesign } from "@expo/vector-icons";

import Colors from "../../../../Resources/Color";
import GlobalStyles from "../../../../Resources/Styles";
import { ProtocolTitles } from "../../../../Resources/Constants";
import { useDevice, useScreenDimensions } from "../../../_layout";
import IconEmptyView from "../../../Components/IconEmptyView";

const COLUMN_TITLE_WIDTH = 160;
const WEEK_COLUMN_WIDTH = 264;
const WEEK_COLUMN_GAP = 40;
const WEEK_LAST_COLUMN_GAP = 16;
const STATS_SUBTLE_BACKGROUND = "rgba(63, 123, 141, 0.025)";

const styles = StyleSheet.create({
  root: {
    width: "100%",
    position: "relative",
  },
  overlayContainer: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    alignItems: "center",
    justifyContent: "center",
    padding: 20,
    backgroundColor: "rgba(255, 255, 255, 0.85)",
    borderRadius: 14,
    zIndex: 5,
  },
  overlayCard: {
    alignItems: "center",
    padding: 24,
    backgroundColor: Colors.whiteColor,
    borderRadius: 14,
    borderWidth: 1,
    borderColor: Colors.borderColor,
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 3,
    maxWidth: 520,
    width: "100%",
  },
  overlayTag: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 10,
    backgroundColor: "rgba(63, 123, 141, 0.1)",
    marginBottom: 14,
  },
  overlayTagText: {
    fontSize: 12,
    color: Colors.bubbleColor,
    fontWeight: "500",
  },
  overlayTitle: {
    fontSize: 16,
    fontWeight: "600",
    marginBottom: 8,
    color: Colors.blackColor,
    textAlign: "center",
  },
  overlayText: {
    fontSize: 13,
    color: Colors.darkgrayColor,
    textAlign: "center",
    lineHeight: 18,
  },
  horizontalScroll: {
    width: "100%",
  },
  horizontalContent: {
    flexGrow: 1,
    paddingHorizontal: 16,
    justifyContent: "space-around",
  },
  gridRoot: {
    alignItems: "center",
    paddingBottom: 8,
  },
  gridHeaderRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
  },
  toggleCell: {
    width: COLUMN_TITLE_WIDTH,
    paddingHorizontal: 8,
    alignItems: "center",
  },
  statsButton: {
    paddingHorizontal: 10,
    paddingVertical: 8,
    borderRadius: 10,
  },
  statsButtonText: {
    fontSize: 12,
    fontWeight: "500",
  },
  weekHeaderCell: {
    width: WEEK_COLUMN_WIDTH,
    alignItems: "flex-start",
    flexDirection: "row",
  },
  weekHeaderBody: {
    alignItems: "center",
    width: "100%",
  },
  weekHeaderTop: {
    maxWidth: WEEK_COLUMN_WIDTH,
    justifyContent: "center",
    flexDirection: "row",
  },
  weekHeaderTitle: {
    textAlign: "center",
    fontSize: 13,
    fontWeight: "500",
    color: Colors.grayColor,
    maxWidth: 220,
  },
  weekDivider: {
    height: 1,
    backgroundColor: Colors.borderColor,
    width: "100%",
    marginVertical: 4,
  },
  weekSubHeader: {
    width: "100%",
    gap: 16,
  },
  weekSubHeaderText: {
    flex: 1,
    textAlign: "center",
    fontSize: 13,
    fontWeight: "500",
    color: Colors.grayColor,
  },
  exerciseGroup: {
    alignItems: "center",
    borderRadius: 14,
    borderWidth: 0.1,
    borderColor: Colors.borderColor,
    paddingTop: 8,
    marginTop: 16,
  },
  protocolRow: {
    width: "100%",
    flexDirection: "row",
    alignItems: "center",
    paddingBottom: 8,
  },
  protocolTitleCell: {
    width: COLUMN_TITLE_WIDTH,
    paddingHorizontal: 24,
    alignItems: "center",
    justifyContent: "center",
  },
  protocolTitleText: {
    textAlign: "center",
    color: Colors.grayColor,
  },
  slotWeekRow: {
    alignItems: "center",
    flexDirection: "row",
    width: WEEK_COLUMN_WIDTH,
    minHeight: 40,
  },
  slotWeekContent: {
    width: "100%",
    gap: 16,
  },
  slotTextCell: {
    alignItems: "center",
    flex: 1,
  },
  slotText: {
    width: "100%",
    color: Colors.blackColor,
    textAlign: "center",
    padding: 4,
  },
  slotTextPlus: {
    width: "100%",
    color: Colors.blackColor,
    textAlign: "center",
    padding: 2,
  },
  protocolTextCell: {
    alignItems: "center",
    flex: 1,
  },
  protocolTextBox: {
    width: "100%",
  },
  protocolTextValue: {
    width: "100%",
    color: Colors.grayColor,
    textAlign: "center",
    padding: 2,
  },
  minusWrapper: {
    width: "100%",
    alignItems: "center",
    borderRadius: 10,
  },
  statsSubContainer: {
    width: "100%",
    marginTop: 2,
    marginBottom: 4,
    paddingVertical: 2,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomLeftRadius: 12,
    borderBottomRightRadius: 12,
    backgroundColor: Colors.whiteColor,
    borderTopWidth: 1,
    borderTopColor: "rgba(63, 123, 141, 0.08)",
  },
  statGroupHeaderRow: {
    width: "100%",
    minHeight: 20,
    justifyContent: "center",
    paddingHorizontal: 24,
    paddingVertical: 2,
    borderTopWidth: 1,
    borderTopColor: "rgba(63, 123, 141, 0.08)",
    backgroundColor: Colors.whiteColor,
  },
  statGroupHeaderText: {
    fontSize: 10,
    fontWeight: "600",
    letterSpacing: 0.5,
    color: Colors.grayColor,
    textTransform: "uppercase",
  },
  statRowContainer: {
    width: "100%",
    minHeight: 24,
    justifyContent: "center",
  },
  statRowEven: {
    backgroundColor: Colors.whiteColor,
  },
  statRowOdd: {
    backgroundColor: STATS_SUBTLE_BACKGROUND,
  },
  statRow: {
    width: "100%",
    minHeight: 24,
    alignItems: "center",
  },
  statTitleCell: {
    alignItems: "flex-start",
    justifyContent: "center",
    width: COLUMN_TITLE_WIDTH,
    paddingHorizontal: 24,
  },
  statTitleText: {
    textAlign: "left",
    fontSize: 10,
    color: Colors.darkgrayColor,
  },
  statValueCell: {
    width: WEEK_COLUMN_WIDTH,
    alignItems: "center",
    justifyContent: "center",
  },
  statValueText: {
    width: "100%",
    textAlign: "center",
    fontSize: 10,
    fontWeight: "500",
    color: Colors.blackColor,
  },
  emptyWrapper: {
    alignItems: "center",
    alignSelf: "center",
    width: "100%",
  },
});

const BlockSummaryOverviewContent = observer(({ block }) => {
  const { t, i18n } = useTranslation();
  const { screenSize } = useScreenDimensions();
  const { isMobile } = useDevice();

  if (!block) {
    return null;
  }

  const hideBlockDashboard = block.client?.coach?.hideBlockDashboard ?? false;
  const weekNumbers = Array.from({ length: block.totalWeeks || 0 }, (_, i) => i + 1);

  const OverviewFeatureOverlay = () => {
    return (
      <View style={styles.overlayContainer} pointerEvents="box-only">
        <View style={styles.overlayCard} pointerEvents="auto">
          <View style={styles.overlayTag}>
            <Ionicons
              name="information-circle-outline"
              size={16}
              color={Colors.bubbleColor}
            />
            <Text style={styles.overlayTagText}>{t("optionalFeature-string")}</Text>
          </View>
          <Text style={styles.overlayTitle}>{t("blockOverview-string")}</Text>
          <Text style={styles.overlayText}>
            {t("blockOverviewAvailableText-string")}
          </Text>
        </View>
      </View>
    );
  };

  const WeekHeader = ({ week }) => {
    const isLastWeek = week === block.totalWeeks;

    return (
      <View
        style={[
          styles.weekHeaderCell,
          { marginRight: isLastWeek ? WEEK_LAST_COLUMN_GAP : WEEK_COLUMN_GAP },
        ]}
      >
        <View style={styles.weekHeaderBody}>
          <View style={styles.weekHeaderTop}>
            <Feather
              name={block.isWeekHidden(week) ? "eye-off" : "eye"}
              size={14}
              color={Colors.grayColor}
              style={{ paddingRight: 8 }}
            />
            <Text style={styles.weekHeaderTitle} numberOfLines={1}>
              {block.getWeekName(week)
                ? block.getWeekName(week)
                : t("weekWithNumber-string", { week })}
            </Text>
          </View>

          <View style={styles.weekDivider} />

          <View style={[GlobalStyles.HStack, styles.weekSubHeader]}>
            <Text style={styles.weekSubHeaderText} numberOfLines={1}>
              Slot
            </Text>
            <Text style={styles.weekSubHeaderText} numberOfLines={1}>
              {t("protocol-string")}
            </Text>
          </View>
        </View>
      </View>
    );
  };

  const SlotText = observer(({ slotExercise }) => {
    if (!slotExercise) {
      return (
        <View style={styles.minusWrapper}>
          <AntDesign name="minus" size={10} color={Colors.blackColor} />
        </View>
      );
    }

    return (
      <>
        <Text
          numberOfLines={3}
          style={[
            styles.slotText,
            {
              fontSize: screenSize === "small" ? 10 : 11,
              lineHeight: screenSize === "small" ? 13 : 14,
            },
          ]}
        >
          {slotExercise.getName(t, i18n.language)}
        </Text>
        {slotExercise.getSecondProtocol && (
          <>
            <Text
              style={[
                styles.slotTextPlus,
                { fontSize: screenSize === "small" ? 10 : 11 },
              ]}
            >
              +
            </Text>
            <Text
              numberOfLines={3}
              style={[
                styles.slotTextPlus,
                {
                  fontSize: screenSize === "small" ? 10 : 11,
                  lineHeight: screenSize === "small" ? 13 : 14,
                },
              ]}
            >
              {slotExercise.getSecondProtocol.getName(t, i18n.language)}
            </Text>
          </>
        )}
      </>
    );
  });

  const ProtocolTextValue = observer(({ slotExercise }) => {
    if (!slotExercise?.protocolText) {
      return (
        <View style={styles.minusWrapper}>
          <AntDesign name="minus" size={10} color={Colors.grayColor} />
        </View>
      );
    }

    return (
      <View
        style={[
          styles.protocolTextBox,
          { marginTop: slotExercise.isSecondaryProtocol ? 10 : 0 },
        ]}
      >
        <Text
          numberOfLines={3}
          style={[
            styles.protocolTextValue,
            { fontSize: screenSize === "small" ? 9 : 10 },
          ]}
        >
          {slotExercise.protocolText}
        </Text>
      </View>
    );
  });

  const ProtocolTextCell = observer(({ slotExercise }) => {
    if (!slotExercise) {
      return (
        <View style={styles.minusWrapper}>
          <AntDesign name="minus" size={10} color={Colors.grayColor} />
        </View>
      );
    }

    return (
      <>
        <ProtocolTextValue slotExercise={slotExercise} />
        {slotExercise.getSecondProtocol && (
          <ProtocolTextValue slotExercise={slotExercise.getSecondProtocol} />
        )}
      </>
    );
  });

  const SlotOfWeek = ({ slotExercise }) => {
    return (
      <View style={styles.slotWeekRow}>
        <View style={[GlobalStyles.HStack, styles.slotWeekContent]}>
          <View style={styles.slotTextCell}>
            <SlotText slotExercise={slotExercise} />
          </View>
          <View style={styles.protocolTextCell}>
            <ProtocolTextCell slotExercise={slotExercise} />
          </View>
        </View>
      </View>
    );
  };

  const ProtocolRow = ({ protocolTitle }) => {
    if (block.hasAnyExerciseForSlot(protocolTitle) !== true) {
      return null;
    }

    return (
      <View style={styles.protocolRow}>
        <View style={styles.protocolTitleCell}>
          <Text
            style={[
              styles.protocolTitleText,
              { fontSize: screenSize === "small" ? 10 : 11 },
            ]}
            numberOfLines={3}
          >
            {t(protocolTitle)}
          </Text>
        </View>

        {weekNumbers.map((week) => (
          <View
            style={{
              marginRight:
                week === block.totalWeeks ? WEEK_LAST_COLUMN_GAP : WEEK_COLUMN_GAP,
            }}
            key={`${protocolTitle}-${week}`}
          >
            <SlotOfWeek slotExercise={block.getSlotOfWeek(protocolTitle, week)} />
          </View>
        ))}
      </View>
    );
  };

  const StatGroupHeader = ({ title, showTopDivider = false }) => {
    return (
      <View
        style={[
          styles.statGroupHeaderRow,
          { borderTopWidth: showTopDivider ? 1 : 0, marginTop: showTopDivider ? 3 : 0 },
        ]}
      >
        <Text style={styles.statGroupHeaderText}>{title}</Text>
      </View>
    );
  };

  const StatRow = ({ stat, getValue, isOdd = false }) => {
    return (
      <View
        style={[
          styles.statRowContainer,
          isOdd ? styles.statRowOdd : styles.statRowEven,
        ]}
      >
        <View style={[GlobalStyles.HStack, styles.statRow]}>
          <View style={styles.statTitleCell}>
            <Text style={styles.statTitleText} numberOfLines={3}>
              {t(stat)}
            </Text>
          </View>

          {weekNumbers.map((week) => (
            <View
              style={[
                styles.statValueCell,
                {
                  marginRight:
                    week === block.totalWeeks
                      ? WEEK_LAST_COLUMN_GAP
                      : WEEK_COLUMN_GAP,
                },
              ]}
              key={`${stat}-${week}`}
            >
              <Text style={styles.statValueText}>{getValue(week)}</Text>
            </View>
          ))}
        </View>
      </View>
    );
  };

  const ExpandedStats = ({ basicExercise }) => {
    return (
      <View style={styles.statsSubContainer}>
        <StatGroupHeader
          title={t("volumeAndStress-string")}
        />
        <StatRow
          stat="stressIndex-string"
          getValue={(week) => block.blockDashboardTotalStressIndex(basicExercise, week)}
          isOdd={true}
        />
        <StatRow
          stat="tonnage-string"
          getValue={(week) => block.blockDashboardAverageTonnage(basicExercise, week)}
          isOdd={false}
        />
        <StatRow
          stat="numberOfSets-string"
          getValue={(week) => block.blockDashboardAverageSeries(basicExercise, week)}
          isOdd={true}
        />
        <StatRow
          stat="numberOfLifts-string"
          getValue={(week) => block.blockDashboardAverageNL(basicExercise, week)}
          isOdd={false}
        />
        <StatGroupHeader
          title={t("primarySessions-string")}
          showTopDivider={true}
        />
        <StatRow
          stat="bestSet-string"
          getValue={(week) => block.blockPrimaryStats(basicExercise, "BestSerie", week)}
          isOdd={true}
        />
        <StatRow
          stat="bestE1rm-string"
          getValue={(week) => block.blockPrimaryStats(basicExercise, "MaxErm", week)}
          isOdd={false}
        />
        <StatRow
          stat="loadIncrease-string"
          getValue={(week) =>
            block.blockPrimaryStats(basicExercise, "WeightIncrease", week)
          }
          isOdd={true}
        />
      </View>
    );
  };

  const ShowStatsButton = () => {
    const [isHovered, setIsHovered] = useState(false);

    return (
      <TouchableOpacity
        onPress={() =>
          block.setProperty("showingOverviewStats", !block.showingOverviewStats)
        }
        style={[
          styles.statsButton,
          {
            backgroundColor: isHovered
              ? "rgba(63, 123, 141, 0.1)"
              : block.showingOverviewStats
                ? "rgba(63, 123, 141, 0.05)"
                : "rgba(63, 123, 141, 0.08)",
          },
        ]}
        onMouseEnter={() => !isMobile && setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        activeOpacity={0.85}
      >
        <Text
          style={[
            styles.statsButtonText,
            {
              color: block.showingOverviewStats
                ? Colors.grayColor
                : Colors.bubbleColor,
            },
          ]}
        >
          {block.showingOverviewStats
            ? t("hideStats-string")
            : t("showStats-string")}
        </Text>
      </TouchableOpacity>
    );
  };

  const hasProtocols = block.protocolsAndExercises?.some((e) => !!e.protocolSlot);

  return (
    <View style={styles.root}>
      {hideBlockDashboard && <OverviewFeatureOverlay />}

      {hasProtocols ? (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.horizontalContent}
          style={[
            styles.horizontalScroll,
            {
              opacity: hideBlockDashboard ? 0.4 : 1,
              minHeight: hideBlockDashboard ? 240 : undefined,
            },
          ]}
        >
          <View style={styles.gridRoot}>
            <View style={styles.gridHeaderRow}>
              <View style={styles.toggleCell}>
                <ShowStatsButton />
              </View>
              {weekNumbers.map((week) => (
                <WeekHeader week={week} key={`week-header-${week}`} />
              ))}
            </View>

            {block.hasAnyExerciseForBasicExercise("squat") && (
              <View style={styles.exerciseGroup}>
                <ProtocolRow protocolTitle={ProtocolTitles.PRIMARY_SQUAT} />
                <ProtocolRow protocolTitle={ProtocolTitles.SECONDARY_SQUAT} />
                <ProtocolRow protocolTitle={ProtocolTitles.TERTIARY_SQUAT} />
                <ProtocolRow protocolTitle={ProtocolTitles.QUATERNARY_SQUAT} />
                <ProtocolRow protocolTitle={ProtocolTitles.QUINARY_SQUAT} />

                {block.showingOverviewStats && (
                  <ExpandedStats basicExercise="squat" />
                )}
              </View>
            )}

            {block.hasAnyExerciseForBasicExercise("benchPress") && (
              <View style={styles.exerciseGroup}>
                <ProtocolRow protocolTitle={ProtocolTitles.PRIMARY_BENCH_PRESS} />
                <ProtocolRow protocolTitle={ProtocolTitles.SECONDARY_BENCH_PRESS} />
                <ProtocolRow protocolTitle={ProtocolTitles.TERTIARY_BENCH_PRESS} />
                <ProtocolRow protocolTitle={ProtocolTitles.QUATERNARY_BENCH_PRESS} />
                <ProtocolRow protocolTitle={ProtocolTitles.QUINARY_BENCH_PRESS} />

                {block.showingOverviewStats && (
                  <ExpandedStats basicExercise="benchPress" />
                )}
              </View>
            )}

            {block.hasAnyExerciseForBasicExercise("deadlift") && (
              <View style={styles.exerciseGroup}>
                <ProtocolRow protocolTitle={ProtocolTitles.PRIMARY_DEADLIFT} />
                <ProtocolRow protocolTitle={ProtocolTitles.SECONDARY_DEADLIFT} />
                <ProtocolRow protocolTitle={ProtocolTitles.TERTIARY_DEADLIFT} />
                <ProtocolRow protocolTitle={ProtocolTitles.QUATERNARY_DEADLIFT} />
                <ProtocolRow protocolTitle={ProtocolTitles.QUINARY_DEADLIFT} />

                {block.showingOverviewStats && (
                  <ExpandedStats basicExercise="deadlift" />
                )}
              </View>
            )}
          </View>
        </ScrollView>
      ) : (
        <View
          style={[
            styles.emptyWrapper,
            {
              opacity: hideBlockDashboard ? 0.4 : 1,
              minHeight: hideBlockDashboard ? 240 : undefined,
            },
          ]}
        >
          <IconEmptyView
            iconLibrary="Ionicons"
            mainText="emptyProtocolsTitle-string"
            subText="emptyProtocolsText-string"
            iconName="barbell-outline"
            marginTop={40}
            marginBottom={8}
          />
        </View>
      )}
    </View>
  );
});

export default BlockSummaryOverviewContent;
